import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';

import PageController from './common/pageController';
import CompanyNameTextField from './companyDetails/companyName';
import CompanySizeSelection from './companyDetails/companySize';
import IndustryTextField from './companyDetails/industry';
import OperationalRegionTextField from './companyDetails/operationalRegion';
import PrimaryContactTextField from './companyDetails/primaryContact';

import { axiosInstanceForTenantAPI } from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';

interface QuickstartCompanyDetailsProps {
  quickstartData: {
    companyName: string;
    primaryContact: string;
    operationalRegion: string;
    industry: string;
    companySize: number;
  };
  slug: string;
  screenNum: number;
  maxScreenNum: number;
  changeScreen: (num: number) => void;
}

interface CompanyDetailsProps {
  companyName: string;
  primaryContact: string;
  operationalRegion: string;
  industry: string;
  companySize: number;
}

interface IndustryObject {
  value: string;
  label: string;
}

interface CountryObject {
  code: string;
  name: string;
}

const roundedGradientButton = {
  borderRadius: '100px',
  width: '164px',
  color: 'black',
  fontWeight: 500,
  fontSize: '20px',
  textTransform: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '100px',
    padding: '2px', //ajust the padding to change the width of the border radius
    background: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
  '&:hover': {
    backgroundImage: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    color: 'white',
  },
};

// returns country code given the country name
const getCountryCode = (countryName: string, list: CountryObject[]) => {
  const country = list.find((item) => item.name === countryName);
  return country?.code || '';
};

const QuickstartCompanyDetails = ({
  quickstartData,
  slug,
  screenNum,
  maxScreenNum,
  changeScreen,
}: QuickstartCompanyDetailsProps) => {
  const intl = useIntl();

  const [industryList, setIndustryList] = useState<IndustryObject[]>([]);
  const [countryList, setCountryList] = useState<CountryObject[]>([]);
  const [companyDetails, setCompanyDetails] = useState<CompanyDetailsProps>({
    companyName: 'Ingenuity',
    primaryContact: '',
    operationalRegion: '',
    industry: '',
    companySize: 100,
  });
  const [detailsValidationError, setDetailsValidationError] = useState({
    companyName: '',
    primaryContact: '',
    operationalRegion: '',
    industry: '',
    companySize: '',
  });
  const [detailsVerified, setDetailsVerified] = useState({
    companyName: false,
    operationalRegion: false,
    industry: false,
  });
  const [requestStatus, setRequestStatus] = useState({
    isLoading: false,
    success: false,
    error: false,
    text: '',
  });

  useEffect(() => {
    const countryCode = getCountryCode(
      quickstartData.operationalRegion,
      countryList,
    );
    setCompanyDetails({
      ...quickstartData,
      operationalRegion: countryCode,
    });
  }, [quickstartData, countryList]);

  const {
    data: industryData,
    error: industryError,
    isLoading: industryIsLoading,
  } = useSWR('tenant/industry-choices', {
    revalidateOnFocus: false,
  });
  const {
    data: countryData,
    error: operationalRegionError,
    isLoading: operationalRegionIsLoading,
  } = useSWR('/countries', { revalidateOnFocus: false });

  useEffect(() => {
    setIndustryList(industryData ?? []);
    setCountryList(countryData ?? []);
  }, [industryData, countryData]);

  const setCompanyNameHandler = (newValue: string) => {
    setCompanyDetails({ ...companyDetails, companyName: newValue });
  };

  const setPrimaryContactHandler = (newValue: string) => {
    setCompanyDetails({ ...companyDetails, primaryContact: newValue });
  };

  const setCompanyRegionHandler = (newValue: string) => {
    setCompanyDetails({ ...companyDetails, operationalRegion: newValue });
  };
  const setCompanyIndustryHandler = (newValue: string) => {
    setCompanyDetails({ ...companyDetails, industry: newValue });
  };

  const setCompanySizeHandler = (newValue: number | null) => {
    if (newValue !== null)
      setCompanyDetails({ ...companyDetails, companySize: newValue });
  };

  const setPage = (pageNum: number) => {
    changeScreen(pageNum);
  };

  const isFieldValidated = () => {
    let errors = detailsValidationError;
    let isValidated = true;
    if (!detailsVerified?.companyName) {
      errors = {
        ...errors,
        companyName: intl.formatMessage({
          defaultMessage: 'Please Check to Verify Company Name',
        }),
      };
      isValidated = false;
    } else errors = { ...errors, companyName: '' };
    if (!detailsVerified?.operationalRegion) {
      errors = {
        ...errors,
        operationalRegion: intl.formatMessage({
          defaultMessage: 'Please Check to Verify Headquarters',
        }),
      };
      isValidated = false;
    } else errors = { ...errors, operationalRegion: '' };
    if (!detailsVerified?.industry) {
      errors = {
        ...errors,
        industry: intl.formatMessage({
          defaultMessage: 'Please Check to Verify Industry',
        }),
      };
      isValidated = false;
    } else if (companyDetails?.industry?.length <= 0) {
      errors = {
        ...errors,
        industry: intl.formatMessage({
          defaultMessage: 'Industry is required',
        }),
      };
      isValidated = false;
    } else errors = { ...errors, industry: '' };
    if (companyDetails?.primaryContact?.length <= 0) {
      errors = {
        ...errors,
        primaryContact: intl.formatMessage({
          defaultMessage: 'Primary Contact Number is required',
        }),
      };
      isValidated = false;
    } else if (companyDetails?.primaryContact?.length <= 6) {
      errors = {
        ...errors,
        primaryContact: intl.formatMessage({
          defaultMessage: 'Primary Contact Number must be valid',
        }),
      };
      isValidated = false;
    } else errors = { ...errors, primaryContact: '' };
    setDetailsValidationError(errors);
    return isValidated;
  };

  const completeProfileHandler = async () => {
    if (!isFieldValidated()) return;
    setRequestStatus({
      isLoading: true,
      success: false,
      error: false,
      text: '',
    });
    try {
      const reqData = {
        industry: companyDetails?.industry,
        max_seats: companyDetails?.companySize,
        hotline_number: companyDetails?.primaryContact,
        active_countries: companyDetails?.operationalRegion,
      };
      const response = await axiosInstanceForTenantAPI(slug).patch(
        '/quickstart-details',
        reqData,
      );
      const status = response.status;
      if (status === 200) {
        setRequestStatus({
          isLoading: false,
          success: true,
          error: false,
          text: '',
        });
        setPage(screenNum + 1);
      }
    } catch (err: unknown) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Tooltip Guide not found on your account',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        text: errorMessage,
      });
    }
  };

  return (
    <Stack
      sx={{
        padding: '90px 64px',
        minHeight: '100vh',
        scrollSnapAlign: 'center',
      }}
    >
      <PageController
        page={screenNum}
        setPage={setPage}
        totalPage={maxScreenNum}
      />

      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk, sans-serif',
          fontSize: '28px',
          fontWeight: 700,
          color: '#181818',
          lineHeight: '150%',
          maxWidth: 650,
          marginBottom: '16px',
        }}
      >
        <FormattedMessage defaultMessage='Confirm your organisations profile!' />
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk, sans-serif',
          fontSize: '18px',
          fontWeight: 400,
          color: '#878787',
          lineHeight: '125%',
          marginBottom: '75px',
        }}
      >
        <FormattedMessage defaultMessage='Please verify your details or make required amendments' />
      </Typography>
      <Stack gap={2} marginBottom={6}>
        <Stack direction='row' gap={1}>
          <Stack flexBasis='50%'>
            <CompanyNameTextField
              value={companyDetails.companyName}
              setValue={setCompanyNameHandler}
              verified={detailsVerified?.companyName}
              setVerified={(value: boolean) =>
                setDetailsVerified({ ...detailsVerified, companyName: value })
              }
              error={detailsValidationError?.companyName}
            />
          </Stack>
          <Stack flexBasis='50%'>
            <PrimaryContactTextField
              value={companyDetails.primaryContact}
              setValue={setPrimaryContactHandler}
              error={detailsValidationError?.primaryContact}
            />
          </Stack>
        </Stack>
        <Stack direction='row' gap={1}>
          <Stack flexBasis='50%'>
            <OperationalRegionTextField
              isLoading={operationalRegionIsLoading}
              list={countryList}
              selected={companyDetails.operationalRegion}
              setValue={setCompanyRegionHandler}
              verified={detailsVerified?.operationalRegion}
              setVerified={(value: boolean) =>
                setDetailsVerified({
                  ...detailsVerified,
                  operationalRegion: value,
                })
              }
              error={operationalRegionError}
              fielderror={detailsValidationError?.operationalRegion}
            />
          </Stack>
          <Stack flexBasis='50%'>
            <IndustryTextField
              isLoading={industryIsLoading}
              error={industryError}
              list={industryList}
              selected={companyDetails.industry}
              setValue={setCompanyIndustryHandler}
              verified={detailsVerified?.industry}
              setVerified={(value: boolean) =>
                setDetailsVerified({ ...detailsVerified, industry: value })
              }
              fielderror={detailsValidationError?.industry}
            />
          </Stack>
        </Stack>
        <CompanySizeSelection
          value={companyDetails.companySize}
          setValue={setCompanySizeHandler}
        />
      </Stack>

      <Button sx={roundedGradientButton} onClick={completeProfileHandler}>
        {requestStatus.isLoading ? (
          <CircularProgress size={20} sx={{ marginY: 1 }} />
        ) : (
          intl.formatMessage({ defaultMessage: 'Proceed' })
        )}
      </Button>
    </Stack>
  );
};

export default QuickstartCompanyDetails;
